import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { departmentsApi, Department } from 'lib/api'
import { cn } from 'lib/utils'
import { Input } from 'components/ui/Input'
import { Check, ChevronDown, Loader2, PlusCircle, Search, X } from 'lucide-react'

interface DepartmentSelectProps {
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  label?: string
  error?: string
  required?: boolean
  placeholder?: string
  disabled?: boolean
  allowClear?: boolean
  className?: string
  defaultDepartments?: string[]
}

type DepartmentOption = { id?: number; name: string }

export function DepartmentSelect({
  value = '',
  onChange,
  onBlur,
  label,
  error,
  required = false,
  placeholder,
  disabled = false,
  allowClear = true,
  className,
  defaultDepartments = [],
}: DepartmentSelectProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [remoteOptions, setRemoteOptions] = useState<DepartmentOption[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const displayPlaceholder =
    placeholder || t('createOffer.form.departmentPlaceholder')

  const handleDocumentClick = useCallback((event: MouseEvent) => {
    if (!containerRef.current) return
    if (containerRef.current.contains(event.target as Node)) return
    setIsOpen(false)
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentClick)
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick)
    }
  }, [handleDocumentClick])

  const loadDepartments = useCallback(
    async (term?: string) => {
      try {
        setIsLoading(true)
        setFetchError(null)
        const response = await departmentsApi.list({
          search: term?.trim() || undefined,
          limit: 25,
        })
        setRemoteOptions(response.data || [])
      } catch (err) {
        console.error('Failed to load departments', err)
        setFetchError(t('departments.fetchError'))
      } finally {
        setIsLoading(false)
      }
    },
    [t]
  )

  useEffect(() => {
    if (!isOpen) return
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadDepartments(searchTerm)
    }, 250)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [isOpen, searchTerm, loadDepartments])

  const handleSelect = (name: string) => {
    onChange?.(name)
    setIsOpen(false)
    setSearchTerm('')
    onBlur?.()
  }

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation()
    onChange?.('')
    setSearchTerm('')
    onBlur?.()
  }

  const handleCreate = async () => {
    const name = searchTerm.trim()
    if (!name) return

    try {
      setIsCreating(true)
      setFetchError(null)
      const response = await departmentsApi.create(name)
      const department = response.data
      setRemoteOptions((prev) => {
        const exists = prev.some(
          (option) => option.name.toLowerCase() === department.name.toLowerCase()
        )
        if (exists) {
          return prev
        }
        return [department, ...prev]
      })
      onChange?.(department.name)
      setIsOpen(false)
      setSearchTerm('')
      onBlur?.()
    } catch (err) {
      console.error('Failed to create department', err)
      setFetchError(t('departments.createError'))
    } finally {
      setIsCreating(false)
    }
  }

  const normalizedDefaults = React.useMemo(() => {
    const seen = new Set<string>()
    const list: DepartmentOption[] = []
    defaultDepartments.forEach((name, idx) => {
      const trimmed = (name || '').trim()
      if (!trimmed) return
      const key = trimmed.toLowerCase()
      if (seen.has(key)) return
      seen.add(key)
      list.push({ id: -(idx + 1), name: trimmed })
    })
    return list
  }, [defaultDepartments])

  const combinedOptions = React.useMemo(() => {
    const seen = new Set<string>()
    const merged: DepartmentOption[] = []
    normalizedDefaults.forEach(option => {
      const key = option.name.toLowerCase()
      if (seen.has(key)) return
      seen.add(key)
      merged.push(option)
    })
    remoteOptions.forEach(option => {
      const trimmed = (option.name || '').trim()
      if (!trimmed) return
      const key = trimmed.toLowerCase()
      if (seen.has(key)) return
      seen.add(key)
      merged.push({ ...option, name: trimmed })
    })
    return merged
  }, [normalizedDefaults, remoteOptions])

  const canCreate =
    searchTerm.trim().length > 0 &&
    !combinedOptions.some(
      (option) => option.name.toLowerCase() === searchTerm.trim().toLowerCase()
    )

  const activeValueLabel = value?.trim().length ? value : ''

  const normalizedSearch = searchTerm.trim().toLowerCase()
  const filteredOptions = React.useMemo(() => {
    if (!normalizedSearch) return combinedOptions
    return combinedOptions.filter(option =>
      option.name.toLowerCase().includes(normalizedSearch)
    )
  }, [combinedOptions, normalizedSearch])

  return (
    <div className="space-y-2" ref={containerRef}>
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="ml-1 text-destructive">*</span>}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-destructive focus:ring-destructive',
            className
          )}
          onClick={() => {
            if (disabled) return
            setIsOpen((prev) => !prev)
          }}
        >
          <span
            className={cn(
              'truncate',
              activeValueLabel ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {activeValueLabel || displayPlaceholder}
          </span>
          <div className="flex items-center gap-1">
            {allowClear && activeValueLabel && (
              <X
                className="h-4 w-4 text-muted-foreground hover:text-foreground"
                onClick={handleClear}
              />
            )}
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-input bg-background shadow-lg">
            <div className="border-b border-border p-2">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t('departments.searchPlaceholder')}
                  className="h-8"
                />
              </div>
            </div>
            <div className="max-h-60 overflow-auto">
              {isLoading ? (
                <div className="flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('common.loading')}
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">
                  {normalizedSearch
                    ? t('departments.noResults', { query: searchTerm })
                    : t('departments.emptyState')}
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={`${option.id ?? option.name}`}
                    type="button"
                    className={cn(
                      'flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground',
                      option.name === activeValueLabel && 'bg-accent/80 text-accent-foreground'
                    )}
                    onClick={() => handleSelect(option.name)}
                  >
                    <span>{option.name}</span>
                    {option.name === activeValueLabel && (
                      <Check className="h-4 w-4" />
                    )}
                  </button>
                ))
              )}
            </div>
            {canCreate && (
              <button
                type="button"
                className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-sm text-primary hover:bg-primary/10"
                onClick={handleCreate}
                disabled={isCreating}
              >
                {isCreating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <PlusCircle className="h-4 w-4" />
                )}
                <span>
                  {t('departments.addNew', { name: searchTerm.trim() })}
                </span>
              </button>
            )}
          </div>
        )}
      </div>
      {(error || fetchError) && (
        <p className="text-sm text-destructive">{error || fetchError}</p>
      )}
    </div>
  )
}

